/** @format */

import {
	Box,
	Typography,
	Button,
	Snackbar,
	Alert,
	Fade,
	CircularProgress,
	Paper,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { buttonColors, colors } from "../../../utility/types/constant";
import {
	createAccountLink,
	getAccountRequirement,
} from "../../../redux/actions/circleAction";
import {
	EnterStripeDetailsForm,
	LoadingSpinner,
} from "../../allComponents/AllComponents";
import { selectAccountRequirements } from "../../../redux/selectors/accountVerification";
import { AppDispatch } from "../../../redux/store/store";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PublicIcon from "@mui/icons-material/Public";

const AccountLinkReturnPage = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { userId } = useParams<{ userId: string }>();
	const { search } = useLocation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [error, setError] = useState(false);
	const [pollingCount, setPollingCount] = useState(0);
	const MAX_POLLING_ATTEMPTS = 6; // Poll for ~30 seconds (6 attempts * 5 seconds)
	// const [onboarded, setOnboarded] = useState(false);
	const {
		currently_due,
		past_due,
		eventually_due,
		isLoading,
		error: reqError,
		identityVerificationStatus,
		countryConfig,
	} = useSelector(selectAccountRequirements);

	const isVerificationProcessing =
		identityVerificationStatus === "processing" ||
		identityVerificationStatus === "pending";

	// Redirect to profile if verification is still processing after max polling attempts
	const redirectToProfileWithProcessingMessage = useCallback(() => {
		navigate(`/user/${userId}?verification=processing`, { replace: true });
	}, [navigate, userId]);

	useEffect(() => {
		const params = new URLSearchParams(search);
		const ok = params.get("onboarding") === "success";

		if (!userId) {
			return navigate(`/`, { replace: true });
		}

		if (ok) {
			setOpen(true);
			dispatch(getAccountRequirement(userId));
		} else {
			setError(true);
		}
	}, [dispatch, navigate, search, userId]);

	// useEffect(() => {
	// 	const resp = dispatch(getAccountRequirement(userId!));
	// 	console.log(resp);
	// }, [userId, dispatch]);

	// Poll for verification status if still processing
	useEffect(() => {
		if (!isVerificationProcessing || pollingCount >= MAX_POLLING_ATTEMPTS) {
			return;
		}

		const pollInterval = setInterval(() => {
			setPollingCount((prev) => prev + 1);
			dispatch(getAccountRequirement(userId!));
		}, 5000); // Poll every 5 seconds

		return () => clearInterval(pollInterval);
	}, [dispatch, userId, isVerificationProcessing, pollingCount]);

	// Redirect to profile if max polling attempts reached and still processing
	useEffect(() => {
		if (pollingCount >= MAX_POLLING_ATTEMPTS && isVerificationProcessing) {
			redirectToProfileWithProcessingMessage();
		}
	}, [
		pollingCount,
		isVerificationProcessing,
		redirectToProfileWithProcessingMessage,
	]);

	const stripeRequirementsStillDue = Array.from(
		new Set([...currently_due, ...past_due, ...eventually_due]),
	);

	// Filter out SSN requirement for non-US countries
	const isUSAccount = countryConfig?.code === "US";
	const visibleRequirements = stripeRequirementsStillDue.filter((r) => {
		// Always hide these
		if (
			r === "individual.verification.document" ||
			r === "external_account" ||
			r === "individual.first_name" ||
			r === "individual.last_name"
		) {
			return false;
		}
		// Hide SSN requirement for non-US countries
		if (r === "individual.ssn_last_4" && !isUSAccount) {
			return false;
		}
		return true;
	});
	const needsStripeInfo = visibleRequirements.length > 0;

	// Dynamic requirement labels based on country
	const getRequirementLabels = (): Record<string, string> => {
		const baseLabels: Record<string, string> = {
			external_account: "Bank Account",
			"individual.first_name": "First Name",
			"individual.last_name": "Last Name",
			"individual.dob.day": "Day of Birth",
			"individual.dob.month": "Month of Birth",
			"individual.dob.year": "Year of Birth",
			"tos_acceptance.date": "Acceptance Date",
			"tos_acceptance.ip": "Acceptance IP",
		};

		// Use country-specific label for SSN/National ID
		if (countryConfig) {
			if (countryConfig.requiresSSN) {
				baseLabels["individual.ssn_last_4"] = "Last 4 of SSN";
			} else {
				// For non-US countries, use the country-specific national ID label
				baseLabels["individual.id_number"] = countryConfig.nationalIdLabel;
			}
		} else {
			// Default fallback
			baseLabels["individual.ssn_last_4"] = "Last 4 of SSN";
		}

		return baseLabels;
	};

	const requirementLabels = getRequirementLabels();

	const getRequirementLabel = (key: string) =>
		requirementLabels[key] || key.replace(/_/g, " ");

	const goToDashboard = () => navigate(`/user/${userId}`, { replace: true });
	const retryOnboarding = () => dispatch<any>(createAccountLink(userId!));

	const handleClose = () => {
		setOpen(false);
		navigate(`/user/${userId}`, { replace: true });
	};

	const handleError = useCallback(() => {
		return navigate(`/user/${userId}`);
	}, [userId, navigate]);

	useEffect(() => {
		if (!error) return;

		const timer = setTimeout(() => {
			handleError();
		}, 2500);

		return () => clearTimeout(timer);
	}, [error, handleError]);

	if (error) {
		return (
			<Box textAlign="center" p={4}>
				<Alert severity="error" sx={{ mb: 2 }}>
					We didn’t receive a confirmation from Stripe. Please finish your bank
					setup.
				</Alert>
				<Button
					variant="contained"
					onClick={retryOnboarding}
					sx={{
						color: colors.white,
						backgroundColor: buttonColors.primary,
						"&:hover": { backgroundColor: buttonColors.primary },
						mr: 2,
					}}>
					Retry Onboarding
				</Button>
				<Button variant="outlined" onClick={goToDashboard}>
					Skip for now
				</Button>
			</Box>
		);
	}

	// Show processing state while verification is being processed by Stripe
	if (isVerificationProcessing && pollingCount < MAX_POLLING_ATTEMPTS) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100vh",
					p: 2,
					textAlign: "center",
				}}>
				<HourglassEmptyIcon sx={{ fontSize: 60, color: colors.blue, mb: 2 }} />
				<Typography variant="h5" gutterBottom>
					Identity Verification in Progress
				</Typography>
				<Typography variant="body1" sx={{ mb: 3, maxWidth: 500 }}>
					Stripe is still verifying your identity. This usually takes a few
					moments. Please wait while we check the status...
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
					<CircularProgress size={24} sx={{ color: colors.blue }} />
					<Typography variant="body2" color="text.secondary">
						Checking verification status ({pollingCount + 1}/
						{MAX_POLLING_ATTEMPTS})...
					</Typography>
				</Box>
				<Button
					variant="outlined"
					onClick={() => redirectToProfileWithProcessingMessage()}
					sx={{ mt: 2 }}>
					Continue to Dashboard
				</Button>
			</Box>
		);
	}

	if (!open) {
		// still waiting for Stripe redirect…
		return (
			<LoadingSpinner
				loading={isLoading}
				size={35}
				sx={{ mt: 5 }}
				color={colors.blue}
			/>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				height: "100vh",
				p: 2,
				textAlign: "center",
			}}>
			<Typography variant="h4" gutterBottom>
				Welcome Aboard!
			</Typography>
			<Typography variant="body1" sx={{ mb: 3 }}>
				Your bank account has been linked successfully.
			</Typography>

			{isLoading ?
				<LoadingSpinner
					loading={isLoading}
					size={35}
					sx={{ mt: 5 }}
					color={colors.blue}
				/>
			: reqError ?
				<Alert severity="error">{reqError}</Alert>
			: needsStripeInfo ?
				<Fade in={true} timeout={600}>
					<Box
						sx={{
							width: "100%",
							maxWidth: 1180,
							mx: "auto",
						}}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "420px minmax(0, 1fr)" },
								gap: 3,
								alignItems: "start",
							}}>
							{/* Details Needed Card */}
							<Paper
								elevation={0}
								sx={{
									borderRadius: "24px",
									overflow: "hidden",
									border: `1px solid ${colors.mainSecondary}22`,
									background:
										"linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
									boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
									backdropFilter: "blur(10px)",
									height: "fit-content",
									position: "relative",
								}}>
								<Box
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										right: 0,
										height: 4,
										background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.mainSecondary} 100%)`,
									}}
								/>

								<Box
									sx={{
										p: 3,
										pb: 2,
										borderBottom: `1px solid ${colors.primary}12`,
									}}>
									<Box
										sx={{
											width: 56,
											height: 56,
											borderRadius: "18px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											background: `linear-gradient(135deg, ${colors.primary}16 0%, ${colors.mainSecondary}16 100%)`,
											mb: 2,
										}}>
										<SecurityOutlinedIcon
											sx={{ fontSize: 30, color: colors.primary }}
										/>
									</Box>

									<Typography
										variant="h5"
										sx={{
											fontWeight: 700,
											color: colors.primary,
											mb: 1,
											letterSpacing: 0.2,
										}}>
										More details needed
									</Typography>

									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
											lineHeight: 1.7,
											fontSize: "0.95rem",
										}}>
										To complete your Stripe verification, we need a few
										additional business details. Once submitted, your account
										setup can continue securely.
									</Typography>
								</Box>

								<Box sx={{ p: 3 }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "flex-start",
											gap: 1.5,
											p: 2,
											mb: 2.5,
											borderRadius: "18px",
											backgroundColor: `${colors.primary}08`,
											border: `1px solid ${colors.primary}14`,
										}}>
										<PublicIcon
											sx={{
												color: colors.mainSecondary,
												fontSize: 22,
												mt: 0.2,
												flexShrink: 0,
											}}
										/>
										<Box>
											<Typography
												variant="subtitle2"
												sx={{
													fontWeight: 700,
													color: colors.primary,
													mb: 0.5,
												}}>
												Why this is required
											</Typography>
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
													lineHeight: 1.65,
													fontSize: "0.875rem",
												}}>
												Stripe requires this information to support secure
												payouts and regulatory compliance based on your region.
											</Typography>
										</Box>
									</Box>

									<Typography
										variant="subtitle2"
										sx={{
											fontWeight: 700,
											color: colors.primary,
											mb: 1.5,
										}}>
										Required information
									</Typography>

									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1.25,
										}}>
										{visibleRequirements.map((f, idx) => (
											<Box
												key={idx}
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1.5,
													p: 1.5,
													borderRadius: "16px",
													backgroundColor: "#fff",
													border: `1px solid ${colors.primary}10`,
													boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)",
												}}>
												<Box
													sx={{
														width: 34,
														height: 34,
														borderRadius: "12px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														background: `linear-gradient(135deg, ${colors.mainSecondary}18 0%, ${colors.primary}12 100%)`,
														flexShrink: 0,
													}}>
													<Box
														sx={{
															width: 8,
															height: 8,
															borderRadius: "50%",
															backgroundColor: colors.mainSecondary,
														}}
													/>
												</Box>

												<Typography
													variant="body2"
													sx={{
														color: "#243145",
														fontWeight: 600,
														lineHeight: 1.45,
													}}>
													{getRequirementLabel(f)}
												</Typography>
											</Box>
										))}
									</Box>
								</Box>
							</Paper>

							{/* Form Card */}
							<Paper
								elevation={0}
								sx={{
									borderRadius: "24px",
									border: `1px solid ${colors.mainSecondary}18`,
									backgroundColor: colors.white,
									boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
									p: { xs: 2, sm: 3 },
								}}>
								<EnterStripeDetailsForm
									userId={userId!}
									countryConfig={countryConfig}
								/>
							</Paper>
						</Box>
					</Box>
				</Fade>
			:	<>
					<Alert
						severity="success"
						sx={{
							mb: 3,
							maxWidth: 600,
							p: 3,
							borderWidth: 2,
							borderStyle: "solid",
							borderColor: "success.main",
							boxShadow: 3,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<Typography
							variant="h5"
							component="div"
							sx={{
								fontWeight: "bold",
								color: "success.dark",
								textAlign: "center",
							}}>
							🎉 All set!
							<br />
							<span style={{ fontSize: 20, fontWeight: 500 }}>
								We may need additional details in the future in order to comply
								with Stripe.
							</span>
						</Typography>
					</Alert>
					<Button
						variant="contained"
						onClick={goToDashboard}
						sx={{
							color: colors.white,
							backgroundColor: buttonColors.primary,
							"&:hover": { backgroundColor: buttonColors.primary },
						}}>
						Go to your Dashboard
					</Button>
					<Snackbar
						open={open}
						autoHideDuration={8000}
						onClose={handleClose}
						anchorOrigin={{ vertical: "top", horizontal: "center" }}>
						<Alert
							onClose={handleClose}
							severity="success"
							sx={{ width: "100%" }}>
							You’re fully onboarded thanks!
						</Alert>
					</Snackbar>
				</>
			}
		</Box>
	);
};

export default AccountLinkReturnPage;
